<template>
  <div
    class="select-wrapper"
    :class="{ 'read-only': readonly }"
  >
    <div
      :id="id"
      ref="triggerRef"
      class="relative form-control select-input !flex items-center justify-between whitespace-nowrap w-full"
      :class="[
        'relative form-control select-input !flex items-center justify-between whitespace-nowrap w-full',
        `form-control-${sizeProp}`,
        `text-${sizeProp}`,
        { expanded: dropdownIsExpanded, 'auto-expand': autoExpand, 'has-error': hasError }
      ]"
      tabindex="0"
      :aria-label="alt"
      @click="!readonly ? toggleDropdown(true) : ''"
    >
      <Icon
        v-if="icon && (!autoHideIcon || (mode === 'multiple' ? selectedItems.length === 0 : modelValue == null))"
        class="select-icon mr-2"
        :type="icon"
      />
      <!-- Updated input wrapper for single vs. multiple mode -->
      <div
        class="relative w-full mr-2 flex flex-wrap items-center"
        :class="{ 'auto-expand': autoExpand }"
      >
        <template v-if="mode === 'multiple'">
          <!-- If a slot is provided, use it -->
          <template v-if="$slots.value && !inputFocused">
            <!-- If no value, show placeholder -->
            <template v-if="selectedItems.length === 0">
              <div class="text-placeholder">{{ placeholder }}</div>
            </template>
            <template v-else>
              <slot
                name="value"
                :value="selectedItems"
              />
            </template>
          </template>
          <template v-else>
            <!-- Default chips / summary behaviour -->
            <template v-if="chips">
              <template v-if="selectedItems.length > 0">
                <!-- Show a Chip for each selected item -->
                <ChipList
                  :items="chipListItems"
                  :max-shown="chipsDisplayed"
                  :can-remove="canRemoveChips"
                  @remove-chip="handleChipRemove"
                />
              </template>
              <template v-else-if="!inputFocused">
                <div class="chips-summary text-placeholder">{{ placeholder }}</div>
              </template>
            </template>
            <template v-else>
              <!-- Show chip summary only if the input is not focused -->
              <template v-if="selectedItems.length === 0 && !inputFocused">
                <div class="chips-summary text-placeholder">{{ placeholder }}</div>
              </template>
              <template v-else-if="!inputFocused">
                <div class="chips-summary">
                  {{ selectedItems.length }}
                  {{ selectedItems.length === 1 ? 'item' : 'items' }}
                  selected
                </div>
              </template>
            </template>
          </template>
        </template>
        <template v-else>
          <!-- Single mode: show placeholder or selected value only when input is not focused -->
          <div
            v-if="!inputFocused && valueIsEmpty && localSearchTerm === ''"
            class="absolute text-placeholder"
            @click.stop="!readonly && toggleDropdown(true)"
          >
            {{ placeholder }}
          </div>
          <div
            v-else-if="!inputFocused && localSearchTerm === '' && modelValue != null"
            class="absolute left-0 w-full selected-value"
            @click.stop="!readonly && toggleDropdown(true)"
          >
            <template v-if="$slots.value">
              <slot
                name="value"
                :value="getSingleSelectedOption() || placeholder"
              />
            </template>
            <template v-else>
              {{ getSingleSelectedOption()?.[labelKey] || placeholder }}
            </template>
          </div>
        </template>
      </div>
      <div class="flex items-center">
        <Icon
          v-if="
            clearable &&
            ((mode === 'multiple' && modelValue && modelValue.length > 0) || (mode === 'single' && !valueIsEmpty))
          "
          type="xmark"
          class="mr-1"
          @click.stop="clearSelection()"
        />
        <Icon type="chevron-down" />
      </div>
    </div>
    <teleport
      to="#dropdown-overlay"
      :disabled="!teleport"
    >
      <div
        v-if="dropdownIsExpanded"
        ref="dropdownRef"
        v-click-away="clickAway"
        :style="[floatingStyles, { maxHeight: dropdownMaxHeight }]"
        class="select-dropdown-wrapper"
        :class="{ 'dropdown-auto-width': !dropdownMatchWidth }"
      >
        <div class="select-dropdown-header">
          <div
            v-if="searchable || (mode === 'multiple' && hasSelection)"
            class="select-dropdown-toolbar"
          >
            <div
              v-if="searchable"
              class="select-dropdown-search-container"
            >
              <input
                ref="searchInput"
                v-model="localSearchTerm"
                class="select-dropdown-search"
                :placeholder="t('ui.common.search')"
                :disabled="readonly"
                autofocus
              />
            </div>
            <!-- Only show Clear once there is something to clear, so it does not compete with empty-state chrome -->
            <button
              v-if="mode === 'multiple' && hasSelection"
              type="button"
              class="select-dropdown-clear"
              @mousedown.stop
              @click.stop="clearSelection"
            >
              {{ t('ui.actions.clear') }}
            </button>
          </div>
          <slot name="dropdownHeader" />
          <DropdownSeparator
            v-if="searchable || $slots.dropdownHeader || (mode === 'multiple' && hasSelection)"
            no-margin
          />
        </div>
        <div
          class="select-dropdown-body"
          :class="{ 'is-grouped': isGrouped }"
        >
          <Empty
            v-if="searchable && localSearchTerm === '' && filteredOptions.length == 0"
            size="sm"
            title="Start typing to search"
            icon="magnifying-glass"
          />
          <Empty
            v-else-if="isGrouped ? flatOptions.length === 0 : !filteredOptions || filteredOptions.length === 0"
            size="md"
          />
          <!-- Grouped options: wrap each group so sticky headers stay inside their own block -->
          <template v-if="isGrouped">
            <div
              v-for="(group, groupIndex) in filteredOptions"
              :key="group.code || group[optionGroupLabel]"
              class="select-dropdown-group"
            >
              <div class="select-dropdown-group-header flex items-center">
                <Checkbox
                  v-if="mode === 'multiple'"
                  :is-checked="isGroupAllSelected(group)"
                  :indeterminate="isGroupPartiallySelected(group)"
                  size="sm"
                  @click.stop
                  @change="groupHeaderClick(group)"
                />
                <div
                  class="group-header-label"
                  @click.stop="mode === 'multiple' && groupHeaderClick(group)"
                >
                  <slot
                    name="optionGroup"
                    :group="group"
                  >
                    <div class="group-header-default">{{ group[optionGroupLabel] }}</div>
                  </slot>
                </div>
              </div>
              <div
                v-for="(child, childIndex) in group[optionGroupChildren]"
                :key="`${groupIndex}-${child[valueKey]}`"
                class="select-dropdown-item select-dropdown-item-grouped flex items-center"
                :class="{
                  'focused-item': getFlatIndex(groupIndex, childIndex) === focusedItemIndex,
                  'selected-item': isItemSelected(child),
                  'has-checkbox': mode === 'multiple'
                }"
                @click.stop="handleOptionClick(child)"
                @mouseover="focusedItemIndex = getFlatIndex(groupIndex, childIndex)"
              >
                <Checkbox
                  v-if="mode === 'multiple'"
                  :is-checked="isOptionSelected(child)"
                  size="sm"
                  @click.stop
                  @change="toggleOption(child)"
                />
                <div>
                  <template v-if="$slots.option">
                    <slot
                      name="option"
                      :option="child"
                    />
                  </template>
                  <template v-else>
                    {{ child[labelKey] }}
                  </template>
                </div>
              </div>
            </div>
          </template>
          <!-- Non-grouped options -->
          <template v-else>
            <div
              v-for="(item, index) in filteredOptions"
              :key="item[valueKey]"
              class="select-dropdown-item"
              :class="{
                'focused-item': index === focusedItemIndex,
                'selected-item': isItemSelected(item)
              }"
              @click.stop="handleOptionClick(item)"
              @mouseover="focusedItemIndex = index"
            >
              <template v-if="$slots.option">
                <slot
                  name="option"
                  :option="item"
                />
              </template>
              <template v-else>
                {{ item[labelKey] }}
              </template>
            </div>
          </template>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { autoUpdate, flip, offset, shift, size as floatingSize, useFloating } from '@floating-ui/vue'
import { onKeyStroke } from '@vueuse/core'
import { computed, inject, nextTick, provide, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ChipList from '@/components/chip/ChipList.vue'
import DropdownSeparator from '@/components/dropdown/DropdownSeparator.vue'
import Empty from '@/components/empty/Empty.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import Icon from '@/components/icon/Icon.vue'
import { useDropdownStack } from '@/composables/useDropdownStack.ts'

const props = defineProps({
  modelValue: {
    // For multiple mode, expect an Array of values.
    type: [String, Number, Object, Array],
    default: null
  },
  options: {
    type: Array,
    default: () => []
  },
  searchable: {
    type: Boolean,
    default: false
  },
  searchTerm: {
    type: String,
    default: ''
  },
  handleSearch: {
    type: Boolean,
    default: true
  },
  onSelectReturnAsObject: {
    type: Boolean,
    default: false
  },
  valueKey: {
    type: String,
    default: 'value'
  },
  labelKey: {
    type: String,
    default: 'label'
  },
  clearable: {
    type: Boolean,
    default: false
  },
  hasError: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: 'Select an option'
  },
  icon: {
    type: String,
    default: null
  },
  id: {
    type: String,
    default: null
  },
  readonly: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'md'
  },
  mode: {
    type: String,
    default: 'single' // or 'multiple'
  },
  // New grouping props:
  optionGroupLabel: {
    type: String,
    default: null
  },
  optionGroupChildren: {
    type: String,
    default: null
  },
  // New props for chips and autoExpand:
  chips: {
    type: Boolean,
    default: true
  },
  chipsDisplayed: {
    type: Number,
    default: 3
  },
  autoExpand: {
    type: Boolean,
    default: false
  },
  // When true, the left-hand icon will be hidden once at least one value is selected
  autoHideIcon: {
    type: Boolean,
    default: false
  },
  alt: {
    type: String,
    default: null
  },
  // When false, dropdown width will be based on content instead of matching trigger width
  dropdownMatchWidth: {
    type: Boolean,
    default: true
  },
  canRemoveChips: {
    type: Boolean,
    default: false
  },
  teleport: {
    type: Boolean,
    default: true
  },
  // Caps the dropdown including the sticky search / header slot
  dropdownMaxHeight: {
    type: String,
    default: '200px'
  }
})
const emit = defineEmits(['update:modelValue', 'update:searchTerm', 'select', 'deselect', 'change'])
const { t } = useI18n()
// ------------ Dropdown stack setup ------------
const dropdownId = Symbol('select')
const parentAncestors = inject('dropdownAncestors', [])
const ancestorsWithSelf = [...parentAncestors, dropdownId]

provide('dropdownAncestors', ancestorsWithSelf)
provide('dropdownId', dropdownId)

const { openDropdown, closeDropdown: unregisterDropdown, isTop } = useDropdownStack()

const triggerRef = ref(null)
const dropdownRef = ref(null)
const searchInput = ref(null)
const inputFocused = ref(false)

const middleware = computed(() => {
  const base = [offset(5), flip(), shift()]
  if (props.dropdownMatchWidth) {
    base.push(
      floatingSize({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`
          })
        }
      })
    )
  }
  return base
})

const { floatingStyles } = useFloating(triggerRef, dropdownRef, {
  placement: 'bottom-start',
  middleware: middleware,
  strategy: 'fixed',
  whileElementsMounted: autoUpdate
})

const dropdownIsExpanded = ref(false)
const localSearchTerm = ref(props.searchTerm)
// Initially, no option is focused (set to -1)
const focusedItemIndex = ref(-1)

const isGrouped = computed(() => !!(props.optionGroupLabel && props.optionGroupChildren))

const standardOptions = computed(() => {
  if (!props.options?.length) return []
  if (isGrouped.value) {
    return props.options.map(group => {
      const children = (group[props.optionGroupChildren] || []).map(child => {
        return typeof child === 'object' ? child : { [props.labelKey]: child, [props.valueKey]: child }
      })
      return { ...group, [props.optionGroupChildren]: children }
    })
  } else {
    return props.options.map(opt => {
      return typeof opt === 'object' ? opt : { [props.labelKey]: opt, [props.valueKey]: opt }
    })
  }
})

const filteredOptions = computed(() => {
  const searchLowered = localSearchTerm.value.toLowerCase().trim()
  if (isGrouped.value) {
    if (!props.handleSearch || searchLowered === '') {
      return standardOptions.value
    }
    return standardOptions.value
      .map(group => {
        const filteredChildren = group[props.optionGroupChildren].filter(child =>
          JSON.stringify(child).toLowerCase().includes(searchLowered)
        )
        return { ...group, [props.optionGroupChildren]: filteredChildren }
      })
      .filter(group => group[props.optionGroupChildren].length > 0)
  } else {
    if (!props.handleSearch || searchLowered === '') {
      return standardOptions.value
    }
    return standardOptions.value.filter(option => JSON.stringify(option).toLowerCase().includes(searchLowered))
  }
})

const valueIsEmpty = computed(() => {
  if (props.modelValue == null) return true
  if (typeof props.modelValue === 'object' && !Array.isArray(props.modelValue)) {
    return props.modelValue[props.valueKey] == null
  }
  return false
})

/** True when multiple-select has at least one chosen value */
const hasSelection = computed(() => Array.isArray(props.modelValue) && props.modelValue.length > 0)

const selectedItems = computed(() => {
  if (props.mode === 'multiple' && Array.isArray(props.modelValue)) {
    const allOptions = isGrouped.value
      ? standardOptions.value.reduce((acc, group) => acc.concat(group[props.optionGroupChildren] || []), [])
      : standardOptions.value
    const seen = new Set()

    // Deduplicate by value so a dealership listed under multiple tags only appears once
    return allOptions.filter(option => {
      const optionValue = option[props.valueKey]
      if (seen.has(optionValue)) {
        return false
      }

      const isSelected = props.modelValue.some(val => {
        const valToCompare = typeof val === 'object' ? val[props.valueKey] : val
        return valToCompare === optionValue
      })

      if (isSelected) {
        seen.add(optionValue)
      }

      return isSelected
    })
  }
  return []
})

const chipListItems = computed(() => {
  if (!selectedItems.value.length) {
    return []
  }
  // generate standardized object for ChipList
  return selectedItems.value.map(item => ({
    label: item[props.labelKey]
  }))
})

function clearSelection() {
  if (props.mode === 'multiple') {
    emit('update:modelValue', [])
    emit('change', props.onSelectReturnAsObject ? [] : [])
  } else {
    emit('update:modelValue', null)
    emit('change', null)
  }
}

const flatOptions = computed(() => {
  if (isGrouped.value) {
    const arr = []
    filteredOptions.value.forEach((group, groupIndex) => {
      group[props.optionGroupChildren].forEach((child, childIndex) => {
        arr.push({ groupIndex, childIndex, option: child })
      })
    })
    return arr
  } else {
    return filteredOptions.value
  }
})

function getFlatIndex(groupIndex, childIndex) {
  let index = 0
  for (let i = 0; i < groupIndex; i++) {
    index += filteredOptions.value[i][props.optionGroupChildren].length
  }
  return index + childIndex
}

function getSingleSelectedOption() {
  if (props.modelValue === null) return null
  const modelValueToCompare = typeof props.modelValue === 'object' ? props.modelValue[props.valueKey] : props.modelValue
  const allOptions = isGrouped.value
    ? standardOptions.value.flatMap(group => group[props.optionGroupChildren] || [])
    : standardOptions.value
  return allOptions.find(option => option[props.valueKey] === modelValueToCompare)
}

function toggleDropdown(shouldBeOpen) {
  if (props.readonly) return
  dropdownIsExpanded.value = shouldBeOpen
  if (shouldBeOpen) {
    openDropdown(
      dropdownId,
      () => {
        dropdownIsExpanded.value = false
        unregisterDropdown(dropdownId)
      },
      parentAncestors
    )
    focusedItemIndex.value = -1
    if (props.searchable) {
      nextTick(() => {
        // Since the input is always visible, focus it.
        searchInput.value.focus()
      })
    }
  } else {
    searchInput.value?.blur()
    localSearchTerm.value = ''
    unregisterDropdown(dropdownId)
  }
}

function clickAway() {
  if (isTop(dropdownId)) {
    toggleDropdown(false)
  }
}

/**
 * Handle a click on an option in either selection mode.
 *
 * @param item - The option that was clicked
 */
function handleOptionClick(item) {
  // Multiple mode toggles membership; single mode selects and closes
  if (props.mode === 'multiple') {
    toggleOption(item)
    return
  }

  selectItem(item)
}

/**
 * Whether an option is the current selection in either mode.
 *
 * @param item - The option to check
 * @returns True when the option matches the current model value
 */
function isItemSelected(item) {
  if (props.mode === 'multiple') {
    return isOptionSelected(item)
  }

  const modelValueToCompare =
    typeof props.modelValue === 'object' && props.modelValue !== null && !Array.isArray(props.modelValue)
      ? props.modelValue[props.valueKey]
      : props.modelValue

  return item[props.valueKey] === modelValueToCompare
}

function selectItem(item) {
  // If modelValue is an object, maintain object structure, otherwise use primitive
  const shouldReturnObject = props.onSelectReturnAsObject
  const itemValue = shouldReturnObject ? item : item[props.valueKey]

  if (props.mode === 'single') {
    emit('update:modelValue', itemValue)
    emit('select', itemValue)
    emit('change', itemValue)
    if (props.searchable && props.mode !== 'multiple') {
      localSearchTerm.value = ''
      searchInput.value?.blur()
    }
    nextTick(() => {
      toggleDropdown(false)
    })
  }
}
function toggleOption(item) {
  if (props.mode !== 'multiple') return
  const shouldReturnObject = props.onSelectReturnAsObject
  // If any value in modelValue is an object, maintain object structure
  const itemValue = shouldReturnObject ? item : item[props.valueKey]

  const current = Array.isArray(props.modelValue) ? props.modelValue : []
  const isSelected = current.some(val => {
    const valToCompare = typeof val === 'object' ? val[props.valueKey] : val
    return valToCompare === item[props.valueKey]
  })

  if (isSelected) {
    const newValue = current.filter(val => {
      const valToCompare = typeof val === 'object' ? val[props.valueKey] : val
      return valToCompare !== item[props.valueKey]
    })
    emit('update:modelValue', newValue)
    emit('deselect', itemValue)
    emit('change', newValue)
  } else {
    const newValue = [...current, itemValue]
    emit('update:modelValue', newValue)
    emit('select', itemValue)
    emit('change', newValue)
  }
}

function isOptionSelected(item) {
  if (!Array.isArray(props.modelValue)) return false
  return props.modelValue.some(val => {
    const valToCompare = typeof val === 'object' ? val[props.valueKey] : val
    return valToCompare === item[props.valueKey]
  })
}

/**
 * Select or deselect every child in a group from the header control.
 *
 * @param group - Grouped option row whose children should be toggled
 */
function groupHeaderClick(group) {
  // Group select-all is only meaningful in multiple mode
  if (props.mode !== 'multiple') return
  const current = Array.isArray(props.modelValue) ? props.modelValue : []
  const children = group[props.optionGroupChildren] || []
  const childValues = children.map(child => child[props.valueKey])
  // Toggle the whole group from the header checkbox / label
  const allSelected = isGroupAllSelected(group)
  let updated
  if (allSelected) {
    updated = current.filter(val => !childValues.includes(val))
  } else {
    updated = Array.from(new Set([...current, ...childValues]))
  }
  emit('update:modelValue', updated)
}

/**
 * Whether every child in a group is currently selected.
 *
 * @param group - Grouped option row
 * @returns True when the group has children and all of them are selected
 */
function isGroupAllSelected(group) {
  const children = group[props.optionGroupChildren] || []
  return children.length > 0 && children.every(child => isOptionSelected(child))
}

/**
 * Whether some, but not all, children in a group are selected.
 *
 * @param group - Grouped option row
 * @returns True when the group checkbox should show an indeterminate state
 */
function isGroupPartiallySelected(group) {
  const children = group[props.optionGroupChildren] || []
  return children.some(child => isOptionSelected(child)) && !isGroupAllSelected(group)
}

/**
 * Handles chip removal from ChipList component
 * @param {number} chipIndex - The index of the chip that was removed
 */
function handleChipRemove(chipIndex) {
  if (props.mode !== 'multiple') return

  // Get the item at the chip index from selectedItems
  const itemToRemove = selectedItems.value[chipIndex]

  if (itemToRemove) {
    toggleOption(itemToRemove)
  }
}

watch(localSearchTerm, newVal => {
  emit('update:searchTerm', newVal)
})

watch(
  () => props.searchTerm,
  newVal => {
    localSearchTerm.value = newVal
  }
)

onKeyStroke('ArrowDown', e => {
  if (!dropdownIsExpanded.value || props.readonly) return
  e.preventDefault()
  if (isGrouped.value) {
    if (focusedItemIndex.value === -1 && flatOptions.value.length > 0) {
      focusedItemIndex.value = 0
    } else if (focusedItemIndex.value < flatOptions.value.length - 1) {
      focusedItemIndex.value++
    }
  } else {
    if (focusedItemIndex.value === -1 && filteredOptions.value.length > 0) {
      focusedItemIndex.value = 0
    } else if (focusedItemIndex.value < filteredOptions.value.length - 1) {
      focusedItemIndex.value++
    }
  }
})

onKeyStroke('ArrowUp', e => {
  if (!dropdownIsExpanded.value || props.readonly) return
  e.preventDefault()
  if (isGrouped.value) {
    if (focusedItemIndex.value === -1 && flatOptions.value.length > 0) {
      focusedItemIndex.value = flatOptions.value.length - 1
    } else if (focusedItemIndex.value > 0) {
      focusedItemIndex.value--
    }
  } else {
    if (focusedItemIndex.value === -1 && filteredOptions.value.length > 0) {
      focusedItemIndex.value = filteredOptions.value.length - 1
    } else if (focusedItemIndex.value > 0) {
      focusedItemIndex.value--
    }
  }
})

onKeyStroke('Enter', e => {
  if (!dropdownIsExpanded.value) {
    if (document.activeElement === triggerRef.value && !props.readonly) {
      toggleDropdown(true)
    }
    return
  }
  if (props.readonly) return
  e.preventDefault()
  if (isGrouped.value) {
    const flatItem = flatOptions.value[focusedItemIndex.value]
    if (flatItem) {
      handleOptionClick(flatItem.option)
    }
  } else {
    const item = filteredOptions.value[focusedItemIndex.value]
    if (item) {
      handleOptionClick(item)
    }
  }
  if (props.mode !== 'multiple') {
    focusedItemIndex.value = -1
  }
})

onKeyStroke('Escape', e => {
  if (!dropdownIsExpanded.value || props.readonly) return
  e.preventDefault()
  toggleDropdown(false)
  focusedItemIndex.value = -1
})

// Avoid shadowing the imported `size` middleware from floating-ui
const sizeProp = props.size
</script>

<style scoped>
.select-wrapper {
  cursor: pointer;
  position: relative;
  width: 100%;
}

.select-wrapper.read-only {
  cursor: not-allowed;
  opacity: 1;
}

.select-input {
  position: relative;
}

.select-input.expanded {
  border: 1px solid var(--border-brand);
  box-shadow: var(--focus-ring-brand);
}

.select-dropdown-wrapper {
  background-color: var(--bg-primary_alt);
  border-radius: var(--rounded-md);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-secondary);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 200px;
  z-index: 10;
}

.select-dropdown-header {
  flex-shrink: 0;
}

.select-dropdown-toolbar {
  display: flex;
  align-items: center;
  gap: var(--s-1);
}

.select-dropdown-toolbar .select-dropdown-search-container {
  flex: 1;
  min-width: 0;
}

.select-dropdown-clear {
  flex-shrink: 0;
  margin-right: var(--s-2);
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-tertiary);
  font-size: var(--text-sm);
  cursor: pointer;
}

.select-dropdown-clear:hover {
  color: var(--text-secondary);
}

.select-dropdown-body {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding: var(--s-1);
}

/* No top padding so sticky headers sit flush and items cannot peek above them */
.select-dropdown-body.is-grouped {
  padding-top: 0;
}

.select-dropdown-wrapper.dropdown-auto-width {
  min-width: 200px;
  width: max-content;
}

.select-dropdown-search-container {
  position: relative;
  padding-left: var(--s-6);
}

.select-dropdown-search-container::before {
  content: '\f002';
  font-family: 'Font Awesome 7 Duotone';
  font-size: var(--text-sm);
  color: var(--text-quaternary);
  position: absolute;
  left: var(--s-3);
  top: 0.725rem;
}

.select-dropdown-search {
  width: 100%;
  padding: var(--s-2) var(--s-2);
  position: relative;
  background-color: transparent;
}

.selected-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-dropdown-group-header {
  padding: var(--s-2) var(--s-2) var(--s-1);
  gap: var(--s-2);
  color: var(--text-tertiary);
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
  letter-spacing: 0.01em;
  background-color: var(--bg-primary_alt);
  position: sticky;
  top: 0;
  z-index: 1;
  cursor: default;
}

.group-header-label {
  min-width: 0;
  cursor: pointer;
}

.group-header-default {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-dropdown-item {
  padding: var(--s-1) var(--s-2);
  margin-bottom: var(--s-05);
  border-radius: var(--rounded);
  padding-right: 30px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}

.select-dropdown-item.flex {
  display: flex;
  gap: var(--s-2);
  align-items: center;
}

.select-dropdown-item.has-checkbox {
  padding-right: var(--s-2);
}

/* Nest grouped options under their header so the list reads as a tree */
.select-dropdown-item-grouped {
  padding-left: var(--s-8);
}

.select-dropdown-item:not(.selected-item):hover,
.focused-item {
  background-color: var(--bg-primary_hover);
}

.selected-item {
  background-color: var(--bg-brand-light);
  position: relative;
}

.selected-item::after {
  content: '\f00c';
  font-family: 'Font Awesome 7 Duotone';
  position: absolute;
  right: var(--s-6);
  color: var(--text-brand);
}

/* Checkboxes already show selection; hide the trailing tick so indicators do not compete */
.select-dropdown-item.has-checkbox.selected-item::after {
  content: none;
}

/* When autoExpand is enabled on the select-input, let it grow */
.select-input.auto-expand {
  height: auto;
  min-height: var(--s-10);
}

/* Styling for the summary text when chips are disabled */
.chips-summary {
  font-size: 0.875rem;
  margin-right: 0.5rem;
}

/* Size variations */
.select-input.form-control-sm {
  height: var(--s-8);
}

.select-input.auto-expand.form-control-sm {
  min-height: var(--s-8);
}

.select-icon {
  color: var(--text-secondary);
}
</style>
